import { MouseEvent, useState } from "react";
import { useLocation } from "react-router-dom";
import { MoreHoriz } from "@mui/icons-material";
import {
  Box,
  Chip,
  IconButton,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  menuItemClasses,
  Typography as T,
} from "@mui/material";
import pluralize from "pluralize";
import { LinkListItem } from "~/components/ListCard";
import ColorCircle from "~/pages/datasets/components/ColorCircle";
import AccentColor from "~/pages/datasets/types/enums/accent-color";
import { CeConfigDomain } from "~/types/interfaces/ce-config";

const DomainListRow = ({
  domain,
  isFirst,
  isLast,
  handleShift,
  handleRemove,
}: {
  domain: CeConfigDomain;
  isFirst: boolean;
  isLast: boolean;
  handleShift: (id: string, direction: "up" | "down") => void;
  handleRemove: (id: string) => void;
}) => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleActionMenuClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  return (
    <LinkListItem
      to={{ pathname: domain.id, search: location.search }}
      rootStyles={{
        ".rowContent": {
          alignItems: "center",
        },
      }}
    >
      <ListItemAvatar sx={{ minWidth: "unset" }}>
        <ColorCircle
          sx={{ backgroundColor: domain.icon ?? AccentColor.BLUE, width: 12, height: 12 }}
        />
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1}>
            {domain.name}
            {isFirst && <Chip label="Primary" size="small" color="default" sx={{ fontSize: 12 }} />}
          </Box>
        }
        primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
      />
      <T variant="body2" color="text.secondary">
        {domain.columns?.length} {pluralize("Column", domain.columns?.length)}
      </T>
      <ListItemIcon sx={{ minWidth: "unset" }}>
        <IconButton
          id={`action-btn-${domain.id}`}
          aria-haspopup="true"
          aria-expanded={anchorEl ? "true" : undefined}
          aria-controls={anchorEl ? `action-menu-${domain.id}` : undefined}
          onClick={handleActionMenuClick}
          sx={{
            p: 0.5,
            opacity: 0,
            transition: `opacity 150ms ease-in-out`,
            "&[aria-expanded='true']": { opacity: 1 },
          }}
        >
          <MoreHoriz />
        </IconButton>
        <Menu
          id={`action-menu-${domain.id}`}
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={(e: Event) => {
            e.stopPropagation();
            setAnchorEl(null);
          }}
          MenuListProps={{
            "aria-labelledby": `action-btn-${domain.id}`,
          }}
        >
          <MenuItem
            disabled={isFirst}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
              handleShift(domain.id!, "up");
            }}
          >
            Move Up
          </MenuItem>
          <MenuItem
            disabled={isLast}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
              handleShift(domain.id!, "down");
            }}
          >
            Move Down
          </MenuItem>
          <MenuItem
            sx={{
              color: "attention.main",
              [`&.${menuItemClasses.root}:hover`]: {
                color: "common.white",
                bgcolor: "attention.main",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(null);
              handleRemove(domain.id!);
            }}
          >
            Delete
          </MenuItem>
        </Menu>
      </ListItemIcon>
    </LinkListItem>
  );
};

const DomainList = ({
  domains,
  handleShift,
  handleRemove,
}: {
  domains?: CeConfigDomain[];
  handleShift: (id: string, direction: "up" | "down") => void;
  handleRemove: (id: string) => void;
}) => {
  return (
    <>
      {domains?.map((domain, i) => (
        <DomainListRow
          key={domain.id}
          domain={domain}
          isFirst={i === 0}
          isLast={i === domains.length - 1}
          handleShift={handleShift}
          handleRemove={handleRemove}
        />
      ))}
    </>
  );
};

export default DomainList;
